import API from '../apiCreate'
import { getParams } from '../getParams'
import {
  CRMNotificationType,
  FilterLeadParam,
  Lead,
  LeadState,
  RequestCreateAutomation,
  RequestCreateField,
  RequestCreateLead,
  RequestCreateLeadPaymentLink,
  RequestCreateNotification,
  RequestCreateStatus,
  RequestCreateTrigger,
  RequestUpdateAutomation,
  RequestUpdateField,
  RequestUpdateItemsSorting,
  RequestUpdateNotification,
  RequestUpdateStatuses,
  RequestUpdateTrigger,
  ResponseAutomations,
  ResponseCheckIsUnused,
  ResponseFields,
  ResponseInsertLead,
  ResponseLead,
  ResponseLeadHistory,
  ResponseLeadPaymentLink,
  ResponseLeadPaymentLinks,
  ResponseLeadPayments,
  ResponseLeads,
  ResponseLeadStatuses,
  ResponseManagers,
  ResponseKanbanSources,
  ResponseNotification,
  ResponseNotifications,
  ResponseStatuses,
} from '../types/crm/crm.types'
import { EmptyResponse, PaginationParam, ResponseWrapper } from '../types/base.types'
import {
  CrmBulkRemoveLeadFunnelsResponse,
  CrmLeadCardResponse,
  CrmCreateFunnelResponse,
  CrmFunnelsAvailableForLeadResponse,
  CrmFunnelsIndexResponse,
  CrmLeadCardInFunnelResponse,
  CrmUpdateLeadCardInFunnelResponse,
  CrmFunnelStatusesResponse,
  CrmFunnelAutomationSettings,
  CrmFunnelAutomationSettingsResponse,
  RequestCreateFunnelStatus,
  RequestUpdateFunnelStatus,
} from '../types/crm/crmFunnels.types'
import type { ResponseCrmLeadFunnelsList } from '../types/crm/crmLeadFunnels.types'
import {
  buildKanbanBoardQueryString,
  buildKanbanColumnItemsQueryString,
  KanbanApiFilters,
} from '@pages/Admin/KanbanCRM/api/kanban.api'
import {
  GetKanbanCardResponse,
  GetKanbanColumnItemsResponse,
  MoveKanbanCardResponse,
} from '@pages/Admin/KanbanCRM/store/kanban.types'
import type { AxiosRequestConfig } from 'axios'
export { API }

type ApiRequestConfig = AxiosRequestConfig & {
  skipGlobalErrorAlert?: boolean
  disableGlobalErrorTextTruncate?: boolean
}

export const crmApi = {
  // После перехода на мультиворонки, этот метод возвращает не список лидов, а список связок лид-воронка-менеджер
  getFunnelLeads: (params: {
    status: LeadState
    filter: FilterLeadParam
    paramsPagination: PaginationParam
  }) => API.get<ResponseCrmLeadFunnelsList>(`crm-lead/get-leads/${getParams(params)}`),

  getLead: (params: { lead_id: number }) =>
    API.get<ResponseLead>(`crm-lead/get-lead/${getParams(params)}`),

  getAllManagers: () =>
    API.get<ResponseManagers>('crm-manager/get-all-managers'),
  getAllStatuses: (params: { type: 'client' | 'lead' | 'all' }) =>
    API.get<ResponseStatuses>(
      `crm-lead-status/get-all-statuses/${getParams(params)}`,
    ),
  getAllFields: (params?: { funnel_id?: number }) =>
    API.get<ResponseFields>(
      `crm-lead-field/get-all-fields${
        params?.funnel_id ? `?funnel_id=${params.funnel_id}` : ''
      }`,
    ),

  getLeadsByUserId: (params: { user_id: number }) =>
    API.get<ResponseLeads>(
      `crm-lead/get-leads-by-user-id/${getParams(params)}`,
    ),

  // только для админ (и junior-admin конечно же)
  getAllLeadsInExcel: (params: { type: LeadState; filter: FilterLeadParam }) =>
    API.get<BlobPart>(
      `crm-lead-excel/get-all-leads-in-excel/${getParams(params)}`,
      {
        responseType: 'blob',
      },
    ),

  // Изменяет поля type и manager_id у лида
  // (если нету у лида менеджера: если МОП - он берёт только себе лидов, если РОП (админ) - он должен передать manager_id)
  changeLeadsState: (
    params: { type: LeadState; manager_id?: number },
    data: { lead_ids: number[] },
  ) =>
    API.patch<EmptyResponse>(
      `crm-lead/change-leads-state/${getParams(params)}`,
      data,
    ),

  // // только для sales-manager (МОП)
  //   takeLead: (leadId: number) =>
  //     API.post<EmptyResponse>(`crm-manager/take-lead?id=${leadId}`),

  // // только для head-of-sales (РОП) или админ
  //   assignLeadToManager: (params: { lead_id: number; manager_id: number }) =>
  //     API.post<EmptyResponse>(`crm-lead/assign-lead-to-manager/${getParams(params)}`),

  updateLeadFields: (
    params: { lead_id: number; funnel_id?: number },
    data: { fields: { [key: string]: string } },
  ) =>
    API.patch<EmptyResponse>(
      `crm-lead/update-lead-fields/${getParams(params)}`,
      data,
    ),

  updateLeadFieldUi: (
    params: { lead_id: number },
    data: { field_id: number; is_collapsed: boolean },
  ) =>
    API.post<
      ResponseWrapper<{
        lead_id: number
        field_id: number
        is_collapsed: boolean
      }>
    >(`crm-lead/update-field-ui/${getParams(params)}`, data),

  updateLead: (
    params: { lead_id: number },
    data: Pick<Lead, 'is_duplicate' | 'user_id'>,
  ) =>
    API.post<EmptyResponse>(`crm-lead/update-lead/${getParams(params)}`, data),

  // только для head-of-sales (РОП) или админ
  removeLeads: (params: { type: LeadState }, data: { lead_ids: number[] }) =>
    API.post<EmptyResponse>(`crm-lead/remove-leads/${getParams(params)}`, data),

  // inserting leads
  generateLeadsTemplate: () =>
    API.get<BlobPart>('crm-lead-excel/generate-leads-template', {
      responseType: 'arraybuffer',
    }),
  insertLeadsExcel: (formData: FormData) =>
    API.post<EmptyResponse>('crm-lead-excel/insert-leads-excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      disableGlobalErrorTextTruncate: true,
    } as ApiRequestConfig),
  insertLead: (data: RequestCreateLead) =>
    API.post<ResponseInsertLead>('crm-lead/insert-lead', data),

  getAllLeadStatuses: (
    params: { lead_id: number }, // с сортировкой, сначала новые
  ) =>
    API.get<ResponseLeadStatuses>(
      `crm-lead-status-history/get-all-lead-statuses/${getParams(params)}`,
    ),
  getAllLeadStatusesByFunnel: (params: {
    lead_id: number
    funnel_id: number
  }) =>
    API.get<ResponseLeadStatuses>(
      `crm-lead-status-history/get-all-lead-statuses/${getParams(params)}`,
    ),
  insertLeadStatus: (params: {
    status_id: number
    lead_funnel_id: number
  }) =>
    API.post<EmptyResponse>(
      `crm-lead-status-history/insert-lead-status/${getParams(params)}`,
    ),
  deleteLeadStatus: (params: { status_history_id: number }) =>
    API.post<EmptyResponse>(
      `crm-lead-status-history/delete-lead-status${getParams(params)}`,
    ),

  sendNotification: (params: { lead_id: number; notification_id: number }) =>
    API.post<EmptyResponse>(`crm/send-notification/${getParams(params)}`),

  getLeadHistory: (
    params: {
      lead_id: number
      paramsPagination: PaginationParam
      changed_by?: number
    }, // с сортировкой, сначала новые
  ) =>
    API.get<ResponseLeadHistory>(
      `crm-lead-history/get-lead-history/${getParams(params)}`,
    ),
  downloadLeadHistoryExcel: (params: { lead_id: number }) =>
    API.get<BlobPart>(
      `crm-lead-history/download-lead-history-excel/${getParams(params)}`,
      {
        responseType: 'blob',
      },
    ),

  kanban: {
    getBoard: (params: {
      funnel_id: number
      filters?: KanbanApiFilters
      columns_limit?: number
      status_limits?: Record<number, number>
    }) => {
      const query = buildKanbanBoardQueryString(params)
      return API.get(`crm-kanban/board?${query}`)
    },

    moveCard: (params: {
      lead_funnel_id: number
      to_status_id: number
      sorting: number
    }) => API.patch<MoveKanbanCardResponse>(`crm-kanban/move-card`, params),

    getMoreColumnItems: (params: {
      funnel_id: number
      status_id: number
      cursor?: string | null
      limit?: number
      filters?: KanbanApiFilters
    }) => {
      const query = buildKanbanColumnItemsQueryString(params)
      return API.get<GetKanbanColumnItemsResponse>(
        `crm-kanban/column-items?${query}`,
      )
    },

    getSources: () => API.get<ResponseKanbanSources>('crm-kanban/sources'),

    getCard: (params: { lead_funnel_id: number }) =>
      API.get<GetKanbanCardResponse>(
        `crm-kanban/card?lead_funnel_id=${params.lead_funnel_id}`,
      ),
  },

  deleteStudAccount: (params: { lead_id: number }) =>
    API.post<EmptyResponse>(
      `crm-lead/delete-student-account/${getParams(params)}`,
    ),
  bindingStudAccount: (params: { lead_id: number; user_id: number }) =>
    API.post<EmptyResponse>(
      `crm-lead/binding-student-account/${getParams(params)}`,
    ),

  searchLeadsForSelect: (params: {
    filter: string[]
    paramsPagination: PaginationParam
  }) => API.get(`crm-lead/search-for-select/${getParams(params)}`),

  //--- Оплаты ---
  getLeadPayments: (params: { user_id: number }) =>
    API.get<ResponseLeadPayments>(
      `crm-lead-payment/get-lead-payments/${getParams(params)}`,
    ),

  //--- Ссылки на оплаты ---

  getLeadPaymentLinks: (params: {
    lead_id: number
    paramsPagination: PaginationParam
  }) =>
    API.get<ResponseLeadPaymentLinks>(
      `crm-lead-payment-link/get-links/${getParams(params)}`,
    ),
  getLeadPaymentLink: (link_id: number) =>
    API.get<ResponseLeadPaymentLink>(
      `crm-lead-payment-link/get-link/${link_id}`,
    ),
  createLeadPaymentLink: (data: RequestCreateLeadPaymentLink) =>
    API.post<EmptyResponse>('crm-lead-payment-link/create-link', data),
  deleteLeadPaymentLink: (link_id: number) =>
    API.post<EmptyResponse>(`crm-lead-payment-link/delete-link/${link_id}`),

  //-------- CRM settings (только РОП и админ) ---------

  // обновление сущесвующих статусов
  updateStatus: (data: RequestUpdateStatuses) =>
    API.put<EmptyResponse>('crm/update-status', data),
  updateStatuses: (data: RequestUpdateStatuses) =>
    API.put<EmptyResponse>('crm-lead-status/update-statuses', data),

  insertStatus: (data: RequestCreateStatus) =>
    API.post<EmptyResponse>('crm-lead-status/insert-status', data),
  deleteStatus: (id: number) =>
    API.post<EmptyResponse>(`crm-lead-status/post-delete?id=${id}`),

  // проверка что статус не использовался и его можно изменить/удалить
  checkStatusIsUnused: (id: number) =>
    API.get<ResponseCheckIsUnused>(
      `crm-lead-status/check-status-is-unused?id=${id}`,
    ),
  updateStatusesSorting: (
    params: { funnel_id: number },
    data: RequestUpdateItemsSorting,
  ) =>
    API.put<EmptyResponse>('crm-lead-status/update-statuses-sorting', {
      funnel_id: params.funnel_id,
      ...data,
    }),

  insertField: (data: RequestCreateField) => {
    const { is_constant: _isConstant, ...payload } = data as RequestCreateField & {
      is_constant?: boolean
    }
    return API.post<EmptyResponse>('crm-lead-field/insert-field', payload)
  },
  updateField: (id: number, data: RequestUpdateField) => {
    const { is_constant: _isConstant, ...payload } = data as RequestUpdateField & {
      is_constant?: boolean
    }
    return API.patch<EmptyResponse>(
      `crm-lead-field/update-field?id=${id}`,
      payload,
    )
  },
  deleteField: (id: number) =>
    API.post<EmptyResponse>(`crm-lead-field/post-delete?id=${id}`),

  // проверка что поле не использовалось в анкетах (лидах), и его можно изменить/удалить
  checkFieldIsUnused: (id: number) =>
    API.get<ResponseCheckIsUnused>(
      `crm-lead-field/check-field-is-unused?id=${id}`,
    ),
  updateFieldsSorting: (data: RequestUpdateItemsSorting) =>
    API.put<EmptyResponse>('crm-lead-field/update-fields-sorting', data),

  //--- Уведомления ---
  getNotifications: (params: {
    type: CRMNotificationType
    paramsPagination: PaginationParam
  }) =>
    API.get<ResponseNotifications>(
      `crm/get-notifications/${getParams(params)}`,
    ),
  getAllNotifications: (params: { type: CRMNotificationType }) =>
    API.get<ResponseNotifications>(
      `crm/get-all-notifications/${getParams(params)}`,
    ),
  getNotification: (id: number) =>
    API.get<ResponseNotification>(`crm/get-notification?id=${id}`),

  insertNotification: (data: RequestCreateNotification) =>
    API.post<EmptyResponse>('crm/insert-notification', data),
  updateNotification: (id: number, data: RequestUpdateNotification) =>
    API.patch<EmptyResponse>(`crm/update-notification?id=${id}`, data),
  updateNotificationsSorting: (data: RequestUpdateItemsSorting) =>
    API.put<EmptyResponse>('crm/update-notifications-sorting', data),

  insertTrigger: (data: RequestCreateTrigger) =>
    API.post<EmptyResponse>('crm/insert-notification-trigger', data),
  updateTrigger: (id: number, data: RequestUpdateTrigger) =>
    API.patch<EmptyResponse>(`crm/update-notification-trigger?id=${id}`, data),

  deleteNotification: (params: { id: number; remove_trigger?: boolean }) =>
    API.post<EmptyResponse>(`crm/delete-notification/${getParams(params)}`),

  //--- Автоматизация ---

  getAllAutomations: () =>
    API.get<ResponseAutomations>('crm-automation/get-all-automations'),

  insertAutomation: (data: RequestCreateAutomation) =>
    API.post<EmptyResponse>('crm-automation/insert-automation', data),
  updateAutomation: (id: number, data: RequestUpdateAutomation) =>
    API.patch<EmptyResponse>(`crm-automation/update-automation?id=${id}`, data),
  deleteAutomation: (id: number) =>
    API.post<EmptyResponse>(`crm-automation/delete-automation?id=${id}`),

  updateAutomationsSorting: (data: RequestUpdateItemsSorting) =>
    API.put<EmptyResponse>('crm-automation/update-automations-sorting', data),

  getFunnelAutomationSettings: () =>
    API.get<CrmFunnelAutomationSettingsResponse>(
      'crm-automation/get-funnel-automation-settings',
    ),

  saveFunnelAutomationSettings: (data: CrmFunnelAutomationSettings) =>
    API.post<CrmFunnelAutomationSettingsResponse>(
      'crm-automation/save-funnel-automation-settings',
      data,
    ),

  leads: {
    addFunnelToLead: (params: {
      lead_id: number
      funnel_id: number
      manager_id?: number | null
    }) =>
      API.post<EmptyResponse>(
        `crm-lead-funnel/add-funnel?leadId=${params.lead_id}`,
        {
          funnel_id: params.funnel_id,
          manager_id: params.manager_id ?? null,
        },
      ),
    bulkAddFunnelToLeads: (params: {
      lead_ids: number[]
      funnel_id: number
      manager_id?: number | null
    }) =>
      API.post<EmptyResponse>('crm-lead-funnel/bulk-add', {
        lead_ids: params.lead_ids,
        funnel_id: params.funnel_id,
        manager_id: params.manager_id ?? null,
      }),
    bulkRemoveFunnelFromLeads: (params: {
      lead_ids: number[]
      funnel_id: number
    }) =>
      API.post<CrmBulkRemoveLeadFunnelsResponse>(
        'crm-lead-funnel/bulk-remove',
        {
          lead_ids: params.lead_ids,
          funnel_id: params.funnel_id,
        },
      ),
    getLeadCardInFunnel: (params: { lead_id: number; funnel_id: number }) =>
      API.get<CrmLeadCardInFunnelResponse>(
        `crm-lead-funnel/card-funnel?leadId=${params.lead_id}&funnelId=${params.funnel_id}`,
      ),
    getLeadCard: (params: { lead_id: number }) =>
      API.get<CrmLeadCardResponse>(
        `crm-lead-funnel/card?leadId=${params.lead_id}`,
      ),
    // Для массовой отправки лидов в воронку "в работе", "обработанные" или "клиенты". Или для массовой смены менеджера лидов.
    changeLeadFunnelsState: (
      params: { type: LeadState; manager_id?: number },
      data: { lead_funnel_ids: number[] },
    ) =>
      API.post<EmptyResponse>(
        `crm-lead-funnel/change-lead-funnels-state?type=${params.type}${
          params.manager_id !== undefined ? `&manager_id=${params.manager_id}` : ''
        }`,
        data,
      ),
    updateLeadCardInFunnel: (
      params: { lead_id: number; funnel_id: number },
      data: {
        current_status_id?: number
        manager_id?: number | null
        next_contact_at?: string | null
        is_primary?: boolean
      },
    ) =>
      API.patch<CrmUpdateLeadCardInFunnelResponse>(
        `crm-lead-funnel/update-card-funnel?leadId=${params.lead_id}&funnelId=${params.funnel_id}`,
        data,
      ),
  },

  funnels: {
    getAllFunnels: () => API.get<CrmFunnelsIndexResponse>('crm-funnel/index'),
    getAvailableForLead: (params: { lead_id: number }) =>
      API.get<CrmFunnelsAvailableForLeadResponse>(
        `crm-funnel/available-for-lead?lead_id=${params.lead_id}`,
      ),

    createFunnel: (name: string) =>
      API.post<CrmCreateFunnelResponse>('crm-funnel/create', { name }),
    updateFunnel: (id: number, name: string) =>
      API.patch<EmptyResponse>(`crm-funnel/update?id=${id}`, { name }),
    deleteFunnelWithPassword: (id: number, confirmation: string) =>
      API.delete<EmptyResponse>(`crm-funnel/delete?id=${id}`, {
        data: { confirmation },
      }),
    sortFunnels: (data: { items: number[] }) =>
      API.patch<EmptyResponse>('crm-funnel/sorting', data),

    statuses: {
      getStatuses: ({ funnel_id }: { funnel_id: number }) =>
        API.get<CrmFunnelStatusesResponse>(
          `crm-funnel/statuses?funnelId=${funnel_id}`,
        ),
      createStatus: ({
        funnel_id,
        data,
        config,
      }: {
        funnel_id: number
        data: RequestCreateFunnelStatus
        config?: ApiRequestConfig
      }) =>
        API.post<EmptyResponse>(
          `crm-funnel/create-status?funnelId=${funnel_id}`,
          data,
          config as any,
        ),
      deleteStatus: ({ id }: { id: number }) =>
        API.delete<EmptyResponse>(`crm-funnel/delete-status?statusId=${id}`),
      updateStatus: ({
        id,
        data,
        config,
      }: {
        id: number
        data: RequestUpdateFunnelStatus
        config?: ApiRequestConfig
      }) =>
        API.patch<EmptyResponse>(
          `crm-funnel/update-status?statusId=${id}`,
          data,
          config as any,
        ),
    },
  },
}
